import React, { lazy, Suspense, useEffect, useState } from 'react';
import { Typography, Button, Grid, Box, CircularProgress } from '@mui/material';
import { Eaton } from '@brightlayer-ui/icons-mui';
import welcomeBackgroundImg from '../../images/homeimage.jpg';
import { PermIdentity } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import utilityCloudi18n from '../../i18n';
import enTranslation from '../../locales/en/translation.json';
import { Constants } from '../../constants/constants';
import { useKeycloak } from '../../contexts/KeycloakContext';
import EmailFirstLogin from '../../components/EmailFirstLogin';
import { setAuthMode, AUTH_MODES, getAuthMode } from '../../utils/authMode';
import { shouldActivateKeycloak } from '../../utils/keycloakLocation';

const AppBar = lazy(() => import('@mui/material/AppBar'));
const Toolbar = lazy(() => import('@mui/material/Toolbar'));

utilityCloudi18n.addResourceBundle('en', 'translation', enTranslation);

export const imposeFrenchLocale = i18n => {
    const userBrowserLanguage = window?.navigator?.language;
    if (userBrowserLanguage?.includes(Constants.frenchLanguage)) {
        i18n.changeLanguage('fr');
        i18n.reloadResources();
    }
}

/**
 * ReactHome Component
 * 
 * Landing page for unauthenticated users (public homepage).
 * 
 * PURPOSE:
 * - Display welcome content and branding
 * - Provide login button that triggers authentication
 * - Detect ?keycloak=true URL parameter to enable Keycloak mode
 * - Handle email-first login flow for Keycloak
 * 
 * DUAL AUTHENTICATION SUPPORT:
 * 
 * This component handles the entry point for both authentication modes:
 * 
 * 1. OKTA MODE (Default):
 *    - User clicks "Log in" button
 *    - Sets authMode to 'okta' in localStorage
 *    - Redirects to /login route
 *    - /login shows OktaRedirectLoginScreen with "Log in" button
 *    - User clicks button → redirects to Okta authorization endpoint
 * 
 * 2. KEYCLOAK MODE (via ?keycloak=true):
 *    - User visits URL with ?keycloak=true parameter
 *    - useEffect detects parameter and sets authMode to 'keycloak'
 *    - Sets 'showKeycloakEmailDialog' flag in localStorage
 *    - Forces page reload to initialize KeycloakProvider
 *    - After reload: component detects flag and shows email dialog
 *    - User enters email → determines IdP based on domain
 *    - Redirects to Keycloak with idpHint parameter
 * 
 * WHY PAGE RELOAD IS REQUIRED FOR KEYCLOAK:
 * When switching from Okta to Keycloak mode, AppRouter needs to unmount
 * the Okta <Security> provider and mount the <KeycloakProvider> instead.
 * A page reload ensures clean provider initialization without React
 * context conflicts or stale authentication state.
 * 
 * EMAIL-FIRST FLOW:
 * Keycloak supports "email-first" authentication where:
 * 1. User enters email address
 * 2. App determines which IdP to use based on email domain
 *    (e.g., @eaton.com → Okta, @contractor.com → Azure AD)
 * 3. Keycloak redirects to appropriate IdP with idpHint
 * 4. User authenticates at their organization's IdP
 * 5. IdP redirects back to Keycloak
 * 6. Keycloak redirects back to BUC Portal
 */
const ReactHome = () => {
    const { t, i18n } = useTranslation();
    const { loginWithEmail } = useKeycloak();
    
    /**
     * Detect Keycloak Mode from URL Parameter
     * 
     * Check if URL contains ?keycloak=true or ?keycloak=1 parameter
     * AND if the current location is allowed to use Keycloak.
     * 
     * LOCATION RESTRICTIONS:
     * Keycloak is only activated when both conditions are met:
     * 1. URL has ?keycloak=true or ?keycloak=1 parameter
     * 2. Location is one of:
     *    - localhost:8415 (development)
     *    - smpservices-dev.eastus.cloudapp.azure.com (development Azure)
     * 
     * For all other locations, the keycloak parameter is ignored.
     * 
     * Example (allowed): http://localhost:8415/?keycloak=true
     * Example (allowed): https://smpservices-dev.eastus.cloudapp.azure.com/?keycloak=true
     * Example (ignored): http://production.example.com/?keycloak=true
     * 
     * Optional: Add &prompt=login to force fresh login even if session exists
     * Example: http://localhost:8415/?keycloak=true&prompt=login
     */
    const urlParams = new URLSearchParams(window.location.search);
    const useKeycloakMode = shouldActivateKeycloak(urlParams);
    const shouldForceLogin = urlParams.get('prompt') === 'login';
    
    /**
     * Email Dialog State
     * 
     * Controls whether to show the EmailFirstLogin component.
     * Set via localStorage flag after Keycloak mode is activated and page reloads.
     * 
     * Show email dialog when:
     * 1. Keycloak mode is active (from localStorage OR URL parameter)
     * 2. AND the localStorage flag is set
     * 
     * This ensures the dialog only appears in Keycloak mode, not in Okta mode.
     * 
     * Flow:
     * 1. User visits with ?keycloak=true → sets flag in localStorage
     * 2. Page reloads with KeycloakProvider
     * 3. This component detects flag AND keycloak mode → shows email dialog
     * 4. User submits email → Keycloak authentication begins
     */
    const isKeycloakActive = useKeycloakMode || getAuthMode() === AUTH_MODES.KEYCLOAK;
    const shouldShowEmailLogin = isKeycloakActive && window.localStorage.getItem('showKeycloakEmailDialog') === 'true';
    const [showEmailLogin, setShowEmailLogin] = useState(shouldShowEmailLogin);

    /**
     * Initialization Effect
     * 
     * Runs once on component mount to:
     * 1. Apply French locale if browser language is French
     * 2. Clear the email dialog flag (consumed on first render)
     * 3. Detect ?keycloak=true parameter and initialize Keycloak mode
     */
    useEffect(() => {
        // Apply French locale based on browser language
        imposeFrenchLocale(i18n);
        
        /**
         * Keycloak Mode Initialization
         * 
         * If URL has ?keycloak=true and we're not already in Keycloak mode:
         * 1. Set authMode to 'keycloak' in localStorage
         * 2. Set flag to show email dialog after reload
         * 3. Force page reload to initialize KeycloakProvider in AppRouter
         * 
         * The reload is CRITICAL because AppRouter needs to unmount Okta's
         * <Security> provider and mount <KeycloakProvider> instead.
         * 
         * If URL also has ?prompt=login, it will force a fresh login even if
         * a Keycloak session exists (handled by KeycloakContext).
         */
        if (useKeycloakMode) {
            const currentAuthMode = getAuthMode();
            if (currentAuthMode !== AUTH_MODES.KEYCLOAK) {
                console.log('🔑 URL parameter detected: keycloak=true - switching to Keycloak mode');
                setAuthMode(AUTH_MODES.KEYCLOAK);
                // Set flag to show email dialog after reload
                window.localStorage.setItem('showKeycloakEmailDialog', 'true');
                // Force page reload to initialize KeycloakProvider
                console.log('🔄 Reloading page to initialize Keycloak provider...');
                // Preserve keycloak=true parameter during reload
                window.location.href = window.location.pathname + '?keycloak=true';
            } else if (shouldForceLogin) {
                // Already in Keycloak mode, but prompt=login forces fresh login
                console.log('🔑 prompt=login detected - ensuring email dialog shows');
                window.localStorage.setItem('showKeycloakEmailDialog', 'true');
                setShowEmailLogin(true);  // Update state to show dialog
            }
            // Note: Don't clear showKeycloakEmailDialog flag here - KeycloakContext needs it
            // It will be cleared when user submits email in handleEmailSubmit
        } else {
            /**
             * Ensure Okta Mode When No Keycloak Parameter
             * 
             * If the URL does NOT have ?keycloak=true, check localStorage.
             * Only switch to Okta mode if localStorage authMode is explicitly set to Okta
             * or if no authMode is set at all.
             * 
             * This allows Keycloak mode to persist via localStorage without needing
             * the URL parameter on every page load.
             */
            const currentAuthMode = getAuthMode();
            
            // If already in Keycloak mode (from localStorage), keep it
            if (currentAuthMode === AUTH_MODES.KEYCLOAK) {
                // Don't interfere with the email dialog state - let shouldShowEmailLogin handle it
                return;  // Stay in Keycloak mode
            }
            
            // Only clear flags and switch to Okta if we're not in Keycloak mode
            window.localStorage.removeItem('showKeycloakEmailDialog');
            setShowEmailLogin(false);
            
            if (currentAuthMode !== AUTH_MODES.OKTA) {
                setAuthMode(AUTH_MODES.OKTA);
                // Reload to initialize OktaProvider if we were in some other mode
                window.location.reload();
            }
        }
    }, [useKeycloakMode, shouldShowEmailLogin, shouldForceLogin, i18n]);

    /**
     * Login Button Click Handler
     * 
     * Handles authentication initiation for both Okta and Keycloak modes.
     * 
     * OKTA MODE (default):
     * - Sets authMode to 'okta' (ensures default is set)
     * - Redirects to /login route
     * - /login route renders OktaRedirectLoginScreen
     * - User clicks "Log in" button on that screen
     * - Okta authentication flow begins
     * 
     * KEYCLOAK MODE (when ?keycloak=true in URL):
     * - Sets authMode to 'keycloak'
     * - Sets flag to show email dialog after reload
     * - Forces page reload to initialize KeycloakProvider
     * - After reload, email dialog is shown automatically
     * 
     * NOTE: This button click only happens when Keycloak mode is activated
     * via URL parameter. Current UX shows single "Log in" button that
     * defaults to Okta unless ?keycloak=true is present.
     */
    const handleButtonClick = () => {
        if (useKeycloakMode) {
            setAuthMode(AUTH_MODES.KEYCLOAK);
            // Set flag to show email dialog after reload
            window.localStorage.setItem('showKeycloakEmailDialog', 'true');
            // Force page reload to initialize KeycloakProvider
            window.location.reload();
        } else {
            // Default: Use Okta flow
            setAuthMode(AUTH_MODES.OKTA);
            // Redirect to login route which will trigger Okta authentication
            window.location.href = '/login';
        }
    };

    /**
     * Email Submission Handler (Keycloak Only)
     * 
     * Called when user submits their email in the EmailFirstLogin dialog.
     * 
     * @param {string} email - User's email address
     * @param {string} idpHint - Identity Provider hint (e.g., 'okta', 'azure-ad')
     * 
     * Flow:
     * 1. EmailFirstLogin component determines IdP from email domain
     * 2. Calls this handler with email and idpHint
     * 3. loginWithEmail() redirects to Keycloak with idpHint parameter
     * 4. Keycloak routes user to appropriate IdP
     */
    const handleEmailSubmit = (email, idpHint) => {
        // Clear the email dialog flag now that user has submitted
        window.localStorage.removeItem('showKeycloakEmailDialog');
        
        // Use the loginWithEmail method that includes IdP hint
        loginWithEmail(email, idpHint);
    };

    const currentYear = '© ' + new Date().getFullYear();

    /**
     * EMAIL DIALOG RENDERING
     * 
     * If showEmailLogin is true (set after Keycloak mode reload):
     * - Show EmailFirstLogin component instead of home page
     * - User enters email and selects/auto-detects IdP
     * - Keycloak authentication begins
     * 
     * This is only shown in Keycloak mode after the page reload.
     */
    if (showEmailLogin) {
        return <EmailFirstLogin onEmailSubmit={handleEmailSubmit} />;
    }

    /**
     * MAIN HOME PAGE RENDERING
     * 
     * Shows the public landing page with:
     * - Eaton branding
     * - Welcome content
     * - "Log in" button (triggers handleButtonClick)
     * - Product information
     * - Footer
     * 
     * NOTE: The dual-button UI (separate Okta/Keycloak buttons) was removed
     * to simplify UX. Current design:
     * - Default: Single "Log in" button → Okta
     * - Keycloak: Add ?keycloak=true to URL → Email dialog
     */

    return (
        <div className="react-home-container" data-testid="react-home-container">
            {/* Header section*/}
            <Box sx={{ flexGrow: 1 }}>
                <Suspense fallback={<CircularProgress />}>
                    <AppBar position="static">
                        <Toolbar>
                            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                                <Eaton className="logoImg" />
                            </Typography>
                            <Button
                                variant="contained"
                                color="warning"
                                startIcon={<PermIdentity />}
                                className="appBar-login-btn loginButton"
                                data-testid="appBar-login-btn"
                                onClick={handleButtonClick}
                            >
                                {t('translation:pages.react_home.login-button')}
                            </Button>
                        </Toolbar>
                    </AppBar>
                </Suspense>
            </Box>
            {/* Image body section*/}
            <div
                className="welcome-body"
                style={{
                    backgroundImage: `linear-gradient(to bottom, rgba(245, 246, 252, 0.0), rgba(0, 0, 0, 0.40)), url(${welcomeBackgroundImg})`,
                }}
                data-testid="welcome-body-img"
            >
                <div className="welcome-content">
                    <Typography className="welcome-content1" variant="h4">
                        {t('translation:pages.react_home.welcome-content.welcome-content1')}
                    </Typography>
                    <Typography className="welcome-content2">
                        {t('translation:pages.react_home.welcome-content.welcome-content2-para1')}
                        {t('translation:pages.react_home.welcome-content.welcome-content2-para2')}
                        <a href="mailto:PSMO-Sales@Eaton.com">
                            {t('translation:pages.react_home.welcome-content.mailto')}
                        </a>
                    </Typography>
                    <Button variant="outlined" className="home-page-hero__cta" data-testid="main-login-btn" onClick={handleButtonClick}>
                        {t('translation:pages.react_home.welcome-content.login-or-create')}
                    </Button>
                </div>
            </div>
            {/* Lower body section*/}
            <div className="welcome-body2">
                <div className="welcome-body2-content">
                    <Grid container spacing={1} columns={16}>
                        <Grid item md={8} xs={16} className="welcome-text1">
                            {t('translation:pages.react_home.welcome-body2-content.welcome-text1')}
                            <div className="h-line"></div>
                        </Grid>
                        <Grid item md={8} xs={16} className="welcome-text2">
                            <ul>
                                <li>{t('translation:pages.react_home.welcome-body2-content.welcome-text2.li1')}</li>
                                <li>{t('translation:pages.react_home.welcome-body2-content.welcome-text2.li2')}</li>
                                <li>{t('translation:pages.react_home.welcome-body2-content.welcome-text2.li3')}</li>
                            </ul>
                        </Grid>
                    </Grid>
                </div>
            </div>
            {/* Footer section*/}
            <footer className="welcome-footer" data-testid="welcome-footer">
                <Typography>{currentYear}{t('translation:pages.react_home.welcome-footer')}</Typography>
            </footer>
        </div>
    )
}
export default ReactHome;
